<template>
  <div class="pt-10 px-4">
    <div class="max-w-xl mx-auto rounded p-6 bg-white border">
      <div class="flex justify-center">
        <div
          class="flex items-center py-2"
          v-if="company && company.logo && company.logo_url"
        >
          <img
            class="h-12 w-auto"
            @error="company.logo = false"
            style="object-fit: scale-down; min-height: 100%"
            :src="company.logo_url"
            :alt="company.name"
          />
        </div>
        <div
          v-else
          class="h-16 w-16 flex rounded-full items-center justify-center"
        >
          <img
            class="h-14 w-14"
            :src="'/images/security.svg'"
            alt="security"
          />
        </div>
      </div>

      <div
        v-if="notice"
        class="bg-amber-50 border border-amber-200 rounded text-amber-800 p-4 mt-6 text-sm flex"
      >
        <ion-icon
          name="alert-circle-outline"
          class="h-5 w-5 shrink-0"
        />
        <span class="grow pl-3">{{ notice }}</span>
      </div>

      <h1 class="text-lg font-bold text-center mt-6">Access your secure client portal</h1>

      <div v-if="sent">
        <div class="bg-primary-100 rounded text-primary-600 p-4 mt-6 flex">
          <span class="h-4 w-4 bg-primary-500 text-white rounded-full flex items-center justify-center shrink-0">
            <ion-icon
              name="checkmark"
              class="h-3 w-3"
            />
          </span>
          <div class="grow pl-4 text-sm">
            <strong class="font-bold">Secure Link Sent</strong>
            <div class="mt-2">
              Link was sent to {{ form.email }}. Please check your inbox for next steps. If you did not receive the
              email, please check your junk mail.
            </div>
            <div class="mt-4">
              <button
                type="button"
                @click.prevent="sent = false"
                class="text-primary-600 font-medium underline hover:text-primary-500"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
      <form
        action="#"
        @submit.prevent="sendEmail"
        class="max-w-md mx-auto"
        v-else
      >
        <div class="mt-6">
          <label
            for="email"
            class="font-medium text-sm"
            >Email Address</label
          >
          <div class="flex items-center">
            <div class="flex-grow">
              <input
                type="text"
                name="email"
                id="email"
                v-model="form.email"
                class="bg-white border border-r-0 w-full h-12 rounded-l focus:outline-none focus:border-primary-500 px-2 text-sm"
                placeholder="Enter your email to get started..."
              />
            </div>
            <div class="flex-shrink-0">
              <button
                class="border border-l-0 h-12 px-6 rounded-r shadow flex items-center font-medium text-sm"
                :class="[
                  !loading
                    ? 'border-primary-600 bg-primary-600 hover:bg-primary-500 focus:border-primary-500 text-white'
                    : 'bg-gray-500 border-gray-500 cursor-not-allowed text-gray-200',
                ]"
                :disabled="loading"
              >
                <span class="mr-2">Continue</span>
                <ion-icon
                  name="arrow-forward"
                  v-if="!loading"
                />
                <client-loading-indicator
                  class="h-4 w-4 fill-gray-100 text-gray-600"
                  v-else
                />
              </button>
            </div>
          </div>
          <p
            class="text-xs text-red-500 mt-1"
            v-if="form.errors.has('email')"
          >
            {{ form.errors.first("email") }}
          </p>
        </div>

        <div class="mt-2">
          <label class="flex items-center space-x-2">
            <input
              type="checkbox"
              v-model="form.remember_me"
              class="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <span>Remember this device</span>
          </label>

          <div class="text-xs text-gray-600 mt-2">
            By logging in, you agree to our
            <a
              href="/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
              class="underline hover:text-primary-600 hover:text-underline"
              >terms of use</a
            >
            and
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              class="underline hover:text-primary-600 hover:text-underline"
              >privacy policy</a
            >.
          </div>
        </div>

        <div class="mt-4 bg-gray-100 flex p-4 rounded text-gray-600">
          <span class="pr-4 text-gray-500">
            <ion-icon name="sparkles-outline"></ion-icon>
          </span>
          <span class="text-sm">
            We'll send you a magic link for a password-less sign in experience. Learn more about
            <a
              href="https://financial-cents.com/security"
              target="_blank"
              rel="noopener noreferrer"
              class="text-primary-600 hover:text-primary-700 font-medium"
              >portal security</a
            >.
          </span>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Form from "../../../js/helpers/Form"
import ClientLoadingIndicator from "../components/client-loading-indicator.vue"
import ShiedLogo from "../components/ShiedLogo.vue"

export default {
  name: "Login",
  components: { ShiedLogo, ClientLoadingIndicator },

  data() {
    return {
      loading: false,
      sent: false,
      form: new Form({
        email: "",
        company: null,
        remember_me: true,
      }),
      company: window.FinancialCents.company || null,
      notice: window.FinancialCents.data?.notice || null,
    }
  },

  methods: {
    async sendEmail() {
      this.loading = true
      try {
        await this.form.post("/api/client-portal/login", {
          company: this.company?.id,
        })
        this.sent = true
      } catch (e) {
        if (!e.response || e.response.status !== 422) {
          alert("Unable to process your request. Please contact us to resolve this issue!")
        }
        console.error(e)
      }
      this.loading = false
    },
  },
}
</script>

<style scoped></style>
